import React, {createContext, useMemo} from 'react';
import PropTypes from 'prop-types';
import useVenmoIntegrationExperiment from './hooks/useVenmoIntegrationExperiment';
import useGiftAdvisorOnOverviewExperiment from './hooks/useGiftAdvisorOnOverviewExperiment';
import useGiftAdvisorV2Experiment from './hooks/useGiftAdvisorV2Experiment';
import useFinancialAccountsExperiment from './hooks/useFinancialAccountsExperiment';
import useOnboardingAddGiftsDesignExperiment from './hooks/useOnboardingAddGiftsDesignExperiment';
import useShopPersonalizedRecommendationsExperiment from './hooks/useShopPersonalizedRecommendationsExperiment';

const Context = createContext({
  experiments: {
    venmoIntegration: {},
    giftAdvisorOnOverview: {},
    giftAdvisorV2: {},
    financialAccounts: {},
    onboardingAddGiftsDesign: {},
    shopPersonalizedRecommendations: {},
  },
});

export default Context;

/**
 * This context will be used to handle all Flipper requests or logic associated
 * with any running experiment.
 *
 * Unless there is a very specific reason/use case, do not render the useExperiment
 * hook anywhere else in the application.
 *
 */
export const ExperimentsContextProvider = ({children}) => {
  const venmoIntegration = useVenmoIntegrationExperiment();
  const giftAdvisorOnOverview = useGiftAdvisorOnOverviewExperiment();
  const giftAdvisorV2 = useGiftAdvisorV2Experiment();
  const financialAccounts = useFinancialAccountsExperiment();
  const onboardingAddGiftsDesign = useOnboardingAddGiftsDesignExperiment();
  const shopPersonalizedRecommendations = useShopPersonalizedRecommendationsExperiment();

  const values = useMemo(
    () => ({
      experiments: {
        venmoIntegration,
        giftAdvisorOnOverview,
        giftAdvisorV2,
        financialAccounts,
        onboardingAddGiftsDesign,
        shopPersonalizedRecommendations,
      },
    }),
    [
      venmoIntegration,
      giftAdvisorOnOverview,
      giftAdvisorV2,
      financialAccounts,
      onboardingAddGiftsDesign,
      shopPersonalizedRecommendations,
    ],
  );

  return <Context.Provider value={values}>{children}</Context.Provider>;
};

ExperimentsContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
